// G-NOTIF-01 — Notifications adapter (login-adapter pattern per docs/19).
// Normalizes the enterprise /api/v1/notifications shapes into the legacy v4
// getNotificationDetails envelope the bell/list/popup already consume:
//   { status: "Success", messageCount: [{ totalCount }], readedMessages, unreadedMessages }
//
// Enterprise item:  { notificationId, subject, body, isRead, createdAt }
// Legacy entry:     { notificationDetailsId, firstName/lastName, payload, invoiceId,
//                     createdDate, isRead/isCompleted, tag, commentId }
//
// Notes / uplift candidates (do NOT fix here — API is frozen for this batch):
// - totalCount (the bell badge) is computed CLIENT-SIDE as the unread item count from one list
//   call (page size 200); the API also exposes GET /notifications/unread-count if we ever want
//   the exact server number in a second request.
// - The enterprise item carries no structured link target; the workflow notify() embeds the
//   invoice PUBLIC_ID (ULID) in the body text, so we parse it out for the click-through.
//   Uplift: add ENTITY_TYPE/ENTITY_PUBLIC_ID columns to NOTIFICATION and surface them.
// - Legacy let the CLIENT insert notification rows (saveNotificationDetails with id 0) for
//   @mentions / exception assignment / notify-approver — IDOR-prone by design. The enterprise
//   API deliberately has no client-create path; creation is server-side (workflow submit /
//   advance via notification.helpers.notify()). legacyCreateNotification() below is a no-op
//   shim so those callers stay wired without 404s until server-side hooks exist (uplift).

import API from "../constants/API";
import { authHeader } from "../common/mainfunction";
import {
  GET_NOTIFICATION_URL,
  MARK_NOTIFICATION_READ_URL,
  MARK_ALL_NOTIFICATIONS_READ_URL,
} from "../common/url";

export interface LegacyNotificationEntry {
  notificationDetailsId: string;
  userId?: number;
  tag?: string;
  payload?: string;
  invoiceId?: string;
  commentId?: number;
  firstName?: string;
  lastName?: string;
  supplierName?: string;
  createdDate?: string;
  profileLogo?: string;
  invoiceStatus?: string;
  isRead?: number;
  isCompleted?: number;
  // Match the components' local NotificationEntry shape (which carries an index signature).
  [key: string]: unknown;
}

export interface LegacyNotificationResponse {
  status: "Success" | "Error";
  messageCount: [{ totalCount: number }];
  readedMessages: LegacyNotificationEntry[];
  unreadedMessages: LegacyNotificationEntry[];
}

interface EnterpriseNotification {
  notificationId: string;
  subject: string | null;
  body: string | null;
  isRead: boolean;
  createdAt: string | null;
}

// Crockford-base32 ULID (the invoice PUBLIC_ID the workflow notify() embeds in the body).
const ULID_RE = /\b[0-9A-HJKMNP-TV-Z]{26}\b/;

function toLegacyEntry(item: EnterpriseNotification): LegacyNotificationEntry {
  const body = item.body ?? "";
  const linkedInvoiceId = body.match(ULID_RE)?.[0];
  return {
    notificationDetailsId: item.notificationId,
    // The components render `${firstName} ${lastName}` as the bold title line and `payload`
    // as the message body — map subject/body onto that so render logic stays untouched.
    firstName: item.subject ?? "Notification",
    lastName: "",
    payload: body || item.subject || "",
    invoiceId: linkedInvoiceId,
    createdDate: item.createdAt ?? undefined,
    isRead: item.isRead ? 1 : 0,
    isCompleted: item.isRead ? 1 : 0,
    tag: "System",
    commentId: 0,
  };
}

const emptyResponse = (status: "Success" | "Error"): LegacyNotificationResponse => ({
  status,
  messageCount: [{ totalCount: 0 }],
  readedMessages: [],
  unreadedMessages: [],
});

// GET /api/v1/notifications -> legacy getNotificationDetails envelope.
// (The axios interceptor has already turned {status:'success',data,page} into
// {status:'Success',data,...} before we see it.)
export async function fetchNotificationDetails(): Promise<LegacyNotificationResponse> {
  const response = await API({
    method: "GET",
    url: GET_NOTIFICATION_URL,
    headers: authHeader(),
    params: { size: 200 },
  });
  const body = response.data;
  if (!body || body.status !== "Success" || !Array.isArray(body.data)) {
    return emptyResponse("Error");
  }
  const entries = (body.data as EnterpriseNotification[]).map(toLegacyEntry);
  const unreadedMessages = entries.filter((e) => !e.isRead);
  const readedMessages = entries.filter((e) => e.isRead);
  return {
    status: "Success",
    messageCount: [{ totalCount: unreadedMessages.length }],
    readedMessages,
    unreadedMessages,
  };
}

// POST /api/v1/notifications/:id/read — replaces legacy saveNotificationDetails(isRead:1).
export function markNotificationRead(notificationId: string | number) {
  return API({
    method: "POST",
    url: MARK_NOTIFICATION_READ_URL(notificationId),
    headers: authHeader(),
  });
}

// POST /api/v1/notifications/read-all — one call instead of the legacy per-item fan-out.
export function markAllNotificationsRead() {
  return API({
    method: "POST",
    url: MARK_ALL_NOTIFICATIONS_READ_URL,
    headers: authHeader(),
  });
}

// Dormant legacy write path — see header note. Resolves immediately; never hits the network.
export async function legacyCreateNotification(_payload: unknown): Promise<void> {
  return;
}
